// noinspection DuplicatedCode
import React, { useState } from 'react';
import {
  faCloudArrowDown,
  faPenToSquare,
  faSpinner,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Fade, Form, Spinner, Table } from 'react-bootstrap';
import {
  ActionButtons,
  BLOCK_EDITOR_DEFAULTS_PROPS_VALUE,
  BLOCK_EDITOR_PROPTYPES,
  ContentBlockEditor,
} from 'components/ContentBlockEditor';
import { Input } from 'components/Forms';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import Alert from 'components/Alert/Alert';
import FileManagerForm from './FileManagerForm';
import './FileBlockEditor.scss';
import useFileBlockEditor from './useFileBlockEditor';

const propTypes = BLOCK_EDITOR_PROPTYPES;
const defaultProps = BLOCK_EDITOR_DEFAULTS_PROPS_VALUE;

export default function FileBlockEditor(props) {
  const [open, setOpen] = useState(false);
  const [fileParam, setFileParam] = useState('');
  const {
    isProcessing,
    handleSubmit,
    onSubmit,
    editFile,
    onEditFileFormUpload,
    onAddFileFormUpload,
    onAddFile,
    deleteFile,
    onFileFormCancel,
    mutationUpdateBlock,
    mutationSaveBlock,
    processTypeLabel,
    control,
    onCancel,
    contentBlock,
    fileList,
    isAddFile,
    isEditFile,
    selectedFile,
  } = useFileBlockEditor(props);

  const onDelete = (id) => {
    setOpen(true);
    setFileParam(id);
  };

  const onDeleteConfirm = () => {
    deleteFile(fileParam);
    setOpen(false);
  };

  return (
    <ContentBlockEditor title={`${processTypeLabel} File Block`}>
      {mutationSaveBlock.isError || mutationUpdateBlock.isError ? (
        <Alert variant="danger" data-testid="Alert-error">
          {mutationSaveBlock.isError
            ? mutationSaveBlock.error.message
            : mutationUpdateBlock.error.message}
          .
        </Alert>
      ) : null}
      {mutationSaveBlock.isSuccess && (
        <Alert variant="success" data-testid="Alert-addSuccess">
          New File Block is now added.
        </Alert>
      )}
      {mutationUpdateBlock.isSuccess && (
        <Alert variant="success" data-testid="Alert-updateSuccess">
          File Block has been updated.
        </Alert>
      )}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-light mb-3 p-3 border">
          <div className="mb-2 d-block">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="title" className="d-block mb-2" aria-label='Block Title'>
              <strong>Block Title</strong>
            </label>
            <Input
              control={control}
              name="title"
              placeholder="Enter the block title"
              disabled={isProcessing()}
            />
          </div>
          <div className="d-block mb-2">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="description" className="d-block mb-2" aria-label='Block Description'>
              <strong>
                Block Description
                <i className="text-muted">(Optional)</i>
              </strong>
            </label>
            <Input
              name="description"
              control={control}
              placeholder="Enter the block description"
              disabled={isProcessing()}
            />
            <div className="text-center">
              <ActionButtons
                isProcessing={isProcessing()}
                onCancel={onCancel}
                isUpdate={contentBlock.id.toString().length > 0}
              />
            </div>
          </div>
        </div>
        <div className="d-flex mb-3 align-content-center align-items-center">
          <div className="d-block flex-grow-1">
            <h4 className="pe-2 mb-0">Files</h4>
          </div>
          <div className="d-block">
            <Button
              type="button"
              className="mt-2 btn btn-primary btn-sm btn-pmi"
              onClick={onAddFile}
              role='button'
              aria-label='Add File'
            >
              Add File
            </Button>
          </div>
        </div>
        <div className="d-block table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Order</th>
                <th>Title</th>
                <th>Description</th>
                <th style={{ width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isProcessing() ? (
                <tr>
                  <td colSpan="4">
                    <div className="text-muted text-center m-0">
                      <Spinner size="sm" animation="border" /> Loading...
                    </div>
                  </td>
                </tr>
              ) : (
                fileList?.length > 0 &&
                fileList.map((fileBlock) => (
                  <tr key={fileBlock.id}>
                    <td>{fileBlock.displayOrder}</td>
                    <td>{fileBlock.title}</td>
                    <td>{fileBlock.description}</td>
                    <td className="p-1">
                      <div className="flex space-around space-between text-center">
                        <Button
                          className="p-1 me-1 text-dark"
                          variant="link"
                          type="button"
                          onClick={() => editFile(fileBlock)}
                          role='button'
                          aria-label='Edit File Button'
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </Button>
                        <Button
                          className="p-1 text-dark"
                          variant="link"
                          type="button"
                          onClick={() => onDelete(fileBlock?.id)}
                          role='button'
                          aria-label='Delete File Button'
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        {fileBlock?.mediaPath?.length > 0 && (
                          <a
                            href={fileBlock.mediaPath}
                            className="btn btn-link p-1 text-dark"
                            target="_new"
                            aria-label='faCloudArrowDownIcon'
                          >
                            <FontAwesomeIcon icon={faCloudArrowDown} />
                          </a>
                        )}
                        {fileBlock?.mediaPath?.length === 0 && (
                          <span
                            className="btn btn-link p-1 text-dark"
                            target="_new"
                            title="File is being process"
                            aria-label='File is being Process'
                          >
                            <FontAwesomeIcon icon={faSpinner} spinPulse />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!isProcessing() && fileList?.length <= 0 && (
                <tr>
                  <td colSpan="10">
                    <p
                      className="text-muted mb-0 text-center"
                      data-testid="Text-noFile"
                      aria-label='No file block listed'
                    >
                      No file block listed.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Form>
      {isAddFile && (
        <div>
          <Fade in>
            <div className="add-file-overlay">
              <FileManagerForm
                onCancel={onFileFormCancel}
                onSubmitFiles={onAddFileFormUpload}
              />
            </div>
          </Fade>
        </div>
      )}
      {isEditFile && selectedFile && (
        <div>
          <Fade in>
            <div className="add-file-overlay">
              <FileManagerForm
                onCancel={onFileFormCancel}
                onSubmitFiles={onEditFileFormUpload}
                fileItem={selectedFile}
              />
            </div>
          </Fade>
        </div>
      )}
      <ModalDialog
        open={open}
        setOpen={setOpen}
        header="Delete File"
        message="You are about to delete a file. Are you sure about it?"
        onConfirm={onDeleteConfirm}
      />
    </ContentBlockEditor>
  );
}

FileBlockEditor.propTypes = propTypes;
FileBlockEditor.defaultProps = defaultProps;
